import React from 'react';
import { useNavigate } from 'react-router-dom';

import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AssignmentIcon from '@mui/icons-material/Assignment';
import GroupOffIcon from '@mui/icons-material/GroupOff';
import QuizIcon from '@mui/icons-material/Quiz';
import {
  Box,
  Card,
  Grid,
  LinearProgress,
  Link,
  Skeleton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';

import { useAuth } from 'contexts/AuthContext';

import { EmptyHint, StatTile, Widget } from 'components/dashboard/DashboardWidgets';
import DomainHero, { HERO_PALETTES } from 'components/dashboard/DomainHero';
import { useDashboardProfile } from 'pages/useDashboardProfile';
import {
  AssignmentListResult,
  ProgressSummaryResult,
  QuestionListResult,
  listAssignment,
  listProgressSummary,
  listQuestion,
} from 'stores/TrainingAdminStoreAdapter';

/**
 * 研修管理ドメインのトップ（DEVELOPER のみ）。
 * APIから取得できる実データのみでウィジェットを構成する:
 * - 概況（課題数 / 設問数）
 * - 課題別クリア率（進捗サマリ ALL）
 * - 設問の登録方式（FORM / KNZ）
 * - 対応言語の分布（設問の extensions 集計）
 * - 未割り当ての課題（要対応）
 */
const TrainingAdminHome: React.FC = () => {
  const navigate = useNavigate();
  const { users } = useAuth();
  const [assignments, setAssignments] = React.useState<AssignmentListResult[]>([]);
  const [questions, setQuestions] = React.useState<QuestionListResult[]>([]);
  const [summaries, setSummaries] = React.useState<ProgressSummaryResult[]>([]);
  const [loading, setLoading] = React.useState(true);
  // ユーザー別の全体進捗（課題横断で cleared/total を合算）
  const [userAgg, setUserAgg] = React.useState<
    { userId: string; userName: string; cleared: number; total: number }[]
  >([]);
  const [userAggLoading, setUserAggLoading] = React.useState(true);
  const [refreshing, setRefreshing] = React.useState(false);

  // 概況・進捗の元データを取得する。初回ロードとヒーローの更新ボタンで共用。
  // 取得後に summaries/assignments が更新されるため、ユーザー別集計の useEffect も自動で再計算される。
  const reload = React.useCallback(async () => {
    try {
      const [a, q, s] = await Promise.all([
        listAssignment({}).catch((): AssignmentListResult[] => []),
        listQuestion({}).catch((): QuestionListResult[] => []),
        listProgressSummary({}).catch((): ProgressSummaryResult[] => []),
      ]);
      setAssignments(a);
      setQuestions(q);
      setSummaries(s);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    reload();
  }, [reload]);

  // ヒーローの更新ボタン: 再取得中はスピナー表示にする
  const handleRefresh = React.useCallback(async () => {
    setRefreshing(true);
    try {
      await reload();
    } finally {
      setRefreshing(false);
    }
  }, [reload]);

  // ユーザー別の全体進捗を集計する。
  // 進捗サマリ ALL には各課題の assignmentId が入るため、課題ごとに
  // listProgressSummary({ assignmentId }) を呼び（BY_ASSIGNMENT＝ユーザー別行）、
  // ユーザー横断で cleared / total を合算して全体進捗率を出す。
  React.useEffect(() => {
    // 非表示課題は集計対象から除外する
    const visibleIds = new Set(assignments.map((a) => a.assignmentId));
    const assignmentIds = summaries
      .filter(
        (s) => s.assignmentId && (s.assignedUserCount ?? 0) > 0 && visibleIds.has(s.assignmentId),
      )
      .map((s) => s.assignmentId as string);
    if (assignmentIds.length === 0) {
      setUserAgg([]);
      setUserAggLoading(false);
      return;
    }
    let cancelled = false;
    setUserAggLoading(true);
    Promise.all(
      assignmentIds.map((assignmentId) =>
        listProgressSummary({ assignmentId }).catch((): ProgressSummaryResult[] => []),
      ),
    )
      .then((results) => {
        if (cancelled) return;
        const map = new Map<string, { userName: string; cleared: number; total: number }>();
        results.flat().forEach((r) => {
          if (!r.userId) return;
          const entry = map.get(r.userId) ?? {
            userName: r.userName ?? '',
            cleared: 0,
            total: 0,
          };
          entry.cleared += r.clearedQuestionCount ?? 0;
          entry.total += r.totalQuestionCount ?? 0;
          if (!entry.userName && r.userName) entry.userName = r.userName;
          map.set(r.userId, entry);
        });
        setUserAgg(
          Array.from(map.entries()).map(([userId, v]) => ({
            userId,
            userName: v.userName || users.find((u) => u.userId === userId)?.userName || userId,
            cleared: v.cleared,
            total: v.total,
          })),
        );
      })
      .finally(() => {
        if (!cancelled) setUserAggLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [summaries, assignments, users]);

  // 表示中（非表示でない）の課題ID集合。集計は全てこの集合に絞る。
  // listAssignment({}) は includeHidden 既定 false のため非表示課題を含まない。
  const visibleAssignmentIds = React.useMemo(
    () => new Set(assignments.map((a) => a.assignmentId)),
    [assignments],
  );

  // --- 課題別クリア率（進捗サマリ ALL・割当ありのみクリア率を持つ）。非表示課題は除外 ---
  const clearRateRows = React.useMemo(
    () =>
      summaries
        .filter((s) => s.assignmentId && visibleAssignmentIds.has(s.assignmentId))
        .map((s) => ({
          assignmentId: s.assignmentId as string,
          name: s.assignmentName ?? '',
          assignedUserCount: s.assignedUserCount ?? 0,
          rate: Math.round((s.averageClearRate ?? 0) * 100),
        }))
        .sort((a, b) => b.rate - a.rate),
    [summaries, visibleAssignmentIds],
  );

  // --- 未割り当ての課題（割当人数 0） ---
  const unassigned = clearRateRows.filter((r) => r.assignedUserCount === 0);

  // --- 割当済みの課題（進捗管理の対象） ---
  const assignedRows = React.useMemo(
    () => clearRateRows.filter((r) => r.assignedUserCount > 0),
    [clearRateRows],
  );

  // ダッシュボード状態（フィルタ・展開）を user_profile(AUTO) で復元/保存する
  const [dashboard, setDashboard] = useDashboardProfile('training-admin-home-dashboard', {
    assignFilter: 'incomplete' as 'incomplete' | 'all',
    assignExpanded: false,
    userFilter: 'incomplete' as 'incomplete' | 'all',
    userExpanded: false,
  });

  // 課題別クリア率の表示切替: 未完了（100%未満）のみ / 全て（既定は前者）＋ もっと見る
  const assignFilter = dashboard.assignFilter;
  const setAssignFilter = React.useCallback(
    (v: 'incomplete' | 'all') => setDashboard((prev) => ({ ...prev, assignFilter: v })),
    [setDashboard],
  );
  const assignExpanded = dashboard.assignExpanded;
  const setAssignExpanded = React.useCallback(
    (updater: boolean | ((v: boolean) => boolean)) =>
      setDashboard((prev) => ({
        ...prev,
        assignExpanded: typeof updater === 'function' ? updater(prev.assignExpanded) : updater,
      })),
    [setDashboard],
  );
  const ASSIGN_COLLAPSED = 6;
  const assignRateData = React.useMemo(
    () => (assignFilter === 'incomplete' ? assignedRows.filter((r) => r.rate < 100) : assignedRows),
    [assignedRows, assignFilter],
  );

  // 全体の平均クリア率（割当済み課題の平均）
  const overallClearRate =
    assignedRows.length === 0
      ? 0
      : Math.round(assignedRows.reduce((s, r) => s + r.rate, 0) / assignedRows.length);

  // 割当済みの受講のべ人数（各課題の割当人数の合計）
  const totalSeats = assignedRows.reduce((s, r) => s + r.assignedUserCount, 0);

  // --- ユーザーごとの進捗率 ---
  // 表示切替: 未完了課題が残っているユーザーのみ / 全ユーザー（既定は前者）
  const userFilter = dashboard.userFilter;
  const setUserFilter = React.useCallback(
    (v: 'incomplete' | 'all') => setDashboard((prev) => ({ ...prev, userFilter: v })),
    [setDashboard],
  );
  const userExpanded = dashboard.userExpanded;
  const setUserExpanded = React.useCallback(
    (updater: boolean | ((v: boolean) => boolean)) =>
      setDashboard((prev) => ({
        ...prev,
        userExpanded: typeof updater === 'function' ? updater(prev.userExpanded) : updater,
      })),
    [setDashboard],
  );
  const USER_COLLAPSED = 6;
  const userProgressData = React.useMemo(() => {
    const rows = userAgg
      .filter((u) => u.total > 0)
      .map((u) => ({
        label: u.userName,
        value: Math.round((u.cleared / u.total) * 100),
        id: u.userId,
      }));
    const filtered = userFilter === 'incomplete' ? rows.filter((r) => r.value < 100) : rows;
    // 進捗の低い順（手当てすべき順）
    return filtered.sort((a, b) => a.value - b.value);
  }, [userAgg, userFilter]);

  return (
    <Box sx={{ height: '100%', overflow: 'auto', pr: 1 }}>
      <Stack spacing={2.5} sx={{ maxWidth: 1100 }}>
        {/* ヒーローヘッダー */}
        <DomainHero
          eyebrow="Training Console · Developer"
          title="Training Management"
          tagline="Author challenges and questions, assign them to your team, and keep an eye on how everyone is progressing — all from one console."
          palette={HERO_PALETTES.training}
          onBack={() => navigate('/')}
          onRefresh={handleRefresh}
          refreshing={refreshing}
        />

        {/* 概況（実数） */}
        <Grid container spacing={2}>
          <Grid size={{ xs: 6, sm: 3 }}>
            <StatTile
              icon={<AssignmentIcon sx={{ fontSize: 18 }} />}
              label="課題"
              value={assignments.length}
              suffix="件"
              color="#1565c0"
              loading={loading}
              onClick={() => navigate('/training-admin/training-assignment')}
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <StatTile
              icon={<QuizIcon sx={{ fontSize: 18 }} />}
              label="設問"
              value={questions.length}
              suffix="件"
              color="#6a1b9a"
              loading={loading}
              onClick={() => navigate('/training-admin/training-question')}
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <StatTile
              icon={<AssignmentIcon sx={{ fontSize: 18 }} />}
              label="割当済みの課題"
              value={clearRateRows.filter((r) => r.assignedUserCount > 0).length}
              suffix="件"
              color="#2e7d32"
              loading={loading}
              onClick={() => navigate('/training-admin/training-progress')}
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <StatTile
              icon={<GroupOffIcon sx={{ fontSize: 18 }} />}
              label="未割り当ての課題"
              value={unassigned.length}
              suffix="件"
              color="#c62828"
              loading={loading}
              onClick={() => navigate('/training-admin/training-assignment')}
            />
          </Grid>
        </Grid>

        {/* 全体クリア率サマリ（割当済み課題の平均）— KPI の直下 */}
        <Card
          elevation={0}
          sx={(t) => ({
            borderRadius: 3,
            p: 2.5,
            border: 1,
            borderColor: 'divider',
            bgcolor: alpha('#1565c0', 0.06),
            ...t.applyStyles('dark', { bgcolor: alpha('#1565c0', 0.14) }),
          })}
        >
          {/* ヘッダー行: タイトル ＋ 右上に進捗ダッシュボード導線 */}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 0.5 }}
          >
            <Typography variant="caption" color="text.secondary" fontWeight={700}>
              割当済み課題の平均クリア率
            </Typography>
            <Link
              component="button"
              variant="caption"
              underline="hover"
              onClick={() => navigate('/training-admin/training-progress')}
              sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}
            >
              進捗ダッシュボード
              <ArrowForwardIcon sx={{ fontSize: 14 }} />
            </Link>
          </Stack>
          <Stack direction="row" alignItems="baseline" spacing={1}>
            <Typography variant="h4" fontWeight={800} sx={{ lineHeight: 1 }}>
              {loading ? '—' : `${overallClearRate}%`}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {assignedRows.length} 課題 / 受講のべ {totalSeats} 名
            </Typography>
          </Stack>
          <LinearProgress
            variant="determinate"
            value={loading ? 0 : overallClearRate}
            color={overallClearRate === 100 ? 'success' : 'primary'}
            sx={{ mt: 1, height: 8, borderRadius: 4 }}
          />
        </Card>

        {/* 課題別クリア率 ／ ユーザーごとの進捗率（50%ずつ横並び） */}
        <Grid container spacing={2}>
          {/* 課題別クリア率（未完了あり / 全て 切替・既定は未完了あり） */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Widget
              fit="content"
              title="課題別クリア率"
              action={
                <Stack direction="row" alignItems="center" spacing={1}>
                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={assignFilter}
                    onChange={(_e, v) => {
                      if (v !== null) {
                        setAssignFilter(v);
                        setAssignExpanded(false);
                      }
                    }}
                    sx={{
                      '& .MuiToggleButton-root': {
                        px: 1,
                        py: 0.25,
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        textTransform: 'none',
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 2,
                      },
                    }}
                  >
                    <ToggleButton value="incomplete">未完了あり</ToggleButton>
                    <ToggleButton value="all">全て</ToggleButton>
                  </ToggleButtonGroup>
                  <Link
                    component="button"
                    variant="caption"
                    underline="hover"
                    onClick={() =>
                      navigate(
                        `/training-admin/training-progress?axis=assignment&filter=${assignFilter}`,
                      )
                    }
                    sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}
                  >
                    進捗ダッシュボード
                    <ArrowForwardIcon sx={{ fontSize: 14 }} />
                  </Link>
                </Stack>
              }
            >
              {loading ? (
                <Stack spacing={1}>
                  {[0, 1, 2].map((i) => (
                    <Skeleton key={i} variant="rounded" height={40} />
                  ))}
                </Stack>
              ) : assignRateData.length === 0 ? (
                <EmptyHint
                  text={
                    assignFilter === 'incomplete'
                      ? '未完了の課題はありません 🎉'
                      : '割り当て済みの課題がありません'
                  }
                />
              ) : (
                <Stack spacing={1.25}>
                  {(assignExpanded
                    ? assignRateData
                    : assignRateData.slice(0, ASSIGN_COLLAPSED)
                  ).map((row) => (
                    <Box
                      key={row.assignmentId}
                      onClick={() =>
                        navigate(
                          `/training-admin/training-progress?assignmentId=${encodeURIComponent(
                            row.assignmentId,
                          )}&assignmentName=${encodeURIComponent(row.name)}&filter=${assignFilter}`,
                        )
                      }
                      sx={{
                        cursor: 'pointer',
                        mx: -1,
                        px: 1,
                        py: 0.5,
                        borderRadius: 1.5,
                        transition: 'background-color 0.15s',
                        '&:hover': { bgcolor: 'action.hover' },
                      }}
                    >
                      <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.25 }}>
                        <Typography variant="body2" noWrap sx={{ flex: 1, mr: 1 }}>
                          {row.name}
                        </Typography>
                        <Stack direction="row" spacing={1} alignItems="baseline">
                          <Typography variant="caption" color="text.secondary">
                            {row.assignedUserCount}名
                          </Typography>
                          <Typography variant="caption" fontWeight={700}>
                            {row.rate}%
                          </Typography>
                        </Stack>
                      </Stack>
                      <LinearProgress
                        variant="determinate"
                        value={row.rate}
                        color={row.rate === 100 ? 'success' : 'primary'}
                        sx={{ height: 6, borderRadius: 3 }}
                      />
                    </Box>
                  ))}
                  {assignRateData.length > ASSIGN_COLLAPSED && (
                    <Box sx={{ textAlign: 'center', mt: 0.5 }}>
                      <Typography
                        component="button"
                        onClick={() => setAssignExpanded((v) => !v)}
                        variant="caption"
                        sx={{
                          cursor: 'pointer',
                          border: 0,
                          bgcolor: 'transparent',
                          color: 'primary.main',
                          fontWeight: 600,
                          py: 0.25,
                          '&:hover': { textDecoration: 'underline' },
                        }}
                      >
                        {assignExpanded
                          ? '折りたたむ'
                          : `もっと見る（残り${assignRateData.length - ASSIGN_COLLAPSED}件）`}
                      </Typography>
                    </Box>
                  )}
                </Stack>
              )}
            </Widget>
          </Grid>

          {/* ユーザーごとの進捗率（未完了ユーザー / 全ユーザー 切替） */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Widget
              fit="content"
              title="ユーザーごとの進捗率"
              action={
                <Stack direction="row" alignItems="center" spacing={1}>
                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={userFilter}
                    onChange={(_e, v) => {
                      if (v !== null) {
                        setUserFilter(v);
                        setUserExpanded(false);
                      }
                    }}
                    sx={{
                      '& .MuiToggleButton-root': {
                        px: 1,
                        py: 0.25,
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        textTransform: 'none',
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 2,
                      },
                    }}
                  >
                    <ToggleButton value="incomplete">未完了あり</ToggleButton>
                    <ToggleButton value="all">全ユーザー</ToggleButton>
                  </ToggleButtonGroup>
                  <Link
                    component="button"
                    variant="caption"
                    underline="hover"
                    onClick={() =>
                      navigate(`/training-admin/training-progress?axis=user&filter=${userFilter}`)
                    }
                    sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}
                  >
                    進捗ダッシュボード
                    <ArrowForwardIcon sx={{ fontSize: 14 }} />
                  </Link>
                </Stack>
              }
            >
              {loading || userAggLoading ? (
                <Stack spacing={1.25}>
                  {[0, 1, 2, 3].map((i) => (
                    <Skeleton key={i} variant="rounded" height={40} />
                  ))}
                </Stack>
              ) : userProgressData.length === 0 ? (
                <EmptyHint
                  text={
                    userFilter === 'incomplete'
                      ? '未完了の課題が残っているユーザーはいません 🎉'
                      : '進捗データのあるユーザーがいません'
                  }
                />
              ) : (
                <Stack spacing={1.25}>
                  {(userExpanded
                    ? userProgressData
                    : userProgressData.slice(0, USER_COLLAPSED)
                  ).map((row) => (
                    <Box
                      key={row.id}
                      onClick={() =>
                        navigate(
                          `/training-admin/training-progress?userId=${encodeURIComponent(
                            row.id,
                          )}&filter=${userFilter}`,
                        )
                      }
                      sx={{
                        cursor: 'pointer',
                        mx: -1,
                        px: 1,
                        py: 0.5,
                        borderRadius: 1.5,
                        transition: 'background-color 0.15s',
                        '&:hover': { bgcolor: 'action.hover' },
                      }}
                    >
                      <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.25 }}>
                        <Typography variant="body2" noWrap sx={{ flex: 1, mr: 1 }}>
                          {row.label}
                        </Typography>
                        <Typography variant="caption" fontWeight={700}>
                          {row.value}%
                        </Typography>
                      </Stack>
                      <LinearProgress
                        variant="determinate"
                        value={row.value}
                        color={row.value === 100 ? 'success' : 'primary'}
                        sx={{ height: 6, borderRadius: 3 }}
                      />
                    </Box>
                  ))}
                  {userProgressData.length > USER_COLLAPSED && (
                    <Box sx={{ textAlign: 'center', mt: 0.5 }}>
                      <Typography
                        component="button"
                        onClick={() => setUserExpanded((v) => !v)}
                        variant="caption"
                        sx={{
                          cursor: 'pointer',
                          border: 0,
                          bgcolor: 'transparent',
                          color: 'primary.main',
                          fontWeight: 600,
                          py: 0.25,
                          '&:hover': { textDecoration: 'underline' },
                        }}
                      >
                        {userExpanded
                          ? '折りたたむ'
                          : `もっと見る（残り${userProgressData.length - USER_COLLAPSED}人）`}
                      </Typography>
                    </Box>
                  )}
                </Stack>
              )}
            </Widget>
          </Grid>
        </Grid>
      </Stack>
    </Box>
  );
};

export default TrainingAdminHome;
